import React, { useState, useEffect } from 'react';
import { Database, Server, Clock, AlertCircle, Check } from 'lucide-react';
import './deployment-options.css';

const DeploymentOptions = ({ tutor, deploymentType, onCancel, onSuccess }) => {
  const [deploymentName, setDeploymentName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [instanceType, setInstanceType] = useState('t3.medium');
  const [port, setPort] = useState(8000);

  useEffect(() => {
    // Generate a default deployment name based on the tutor
    if (tutor) {
      setDeploymentName(`${tutor.name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}`);
    }
  }, [tutor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = deploymentType === 'aws' 
        ? '/api/lms/deploy/aws' 
        : '/api/lms/deploy/local';
      
      const payload = {
        model_id: tutor.id,
        deployment_name: deploymentName,
        ...(deploymentType === 'aws' ? { instance_type: instanceType } : { port: parseInt(port) })
      };
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        setDeploymentStatus(data);
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        setError(data.error || 'Deployment failed');
      }
    } catch (err) {
      setError('Error initiating deployment');
      console.error('Deployment error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="deployment-options-container">
      <div className="deployment-header">
        <h3>
          {deploymentType === 'aws' ? (
            <>
              <Server className="icon" size={18} />
              Deploy to AWS
            </>
          ) : (
            <>
              <Database className="icon" size={18} />
              Deploy Locally
            </>
          )}
        </h3>
        <p className="deployment-subtitle">
          {deploymentType === 'aws' 
            ? 'Deploy this tutor to AWS for scalable cloud access'
            : 'Deploy this tutor to your local machine for testing and development'
          }
        </p>
      </div>

      {!success ? (
        <form onSubmit={handleSubmit} className="deployment-form">
          <div className="form-group">
            <label htmlFor="deployment-name">Deployment Name</label>
            <input
              type="text"
              id="deployment-name"
              value={deploymentName}
              onChange={(e) => setDeploymentName(e.target.value)}
              required
              placeholder="e.g., algebra-tutor-production"
            />
          </div>

          {deploymentType === 'aws' ? (
            <div className="form-group">
              <label htmlFor="instance-type">Instance Type</label>
              <select
                id="instance-type"
                value={instanceType}
                onChange={(e) => setInstanceType(e.target.value)}
              >
                <option value="t3.medium">t3.medium (2 vCPU, 4 GB RAM) - Development</option>
                <option value="t3.large">t3.large (2 vCPU, 8 GB RAM) - Small Production</option>
                <option value="t3.xlarge">t3.xlarge (4 vCPU, 16 GB RAM) - Medium Production</option>
                <option value="t3.2xlarge">t3.2xlarge (8 vCPU, 32 GB RAM) - Large Production</option>
              </select>
              <p className="help-text">Select an instance type based on expected traffic</p>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="port-number">Port Number</label>
              <input
                type="number"
                id="port-number"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                min="1024"
                max="65535"
                required
              />
              <p className="help-text">The local port to run the tutor on (1024-65535)</p>
            </div>
          )}

          <div className="deployment-info">
            <h4>Deployment Summary</h4>
            <p>
              <strong>Tutor:</strong> {tutor?.name}
            </p>
            <p>
              <strong>Subject:</strong> {tutor?.subject}
            </p>
            <p>
              <strong>Deployment Type:</strong> {deploymentType === 'aws' ? 'AWS Cloud' : 'Local Server'}
            </p>
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="deployment-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="deploy-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Deploying...
                </>
              ) : (
                <>Deploy</>
              )}
            </button>
          </div>
        </form>
      ) : (
        <div className="deployment-success">
          <div className="success-icon">
            <Check size={32} color="white" />
          </div>
          <h4>Deployment Initiated!</h4>
          <p>Your tutor is being deployed and will be available shortly.</p>
          
          <div className="deployment-details">
            <p>
              <strong>Deployment ID:</strong> {deploymentStatus?.deployment_id}
            </p>
            <p>
              <strong>Status:</strong> {deploymentStatus?.status}
            </p>
            <p>
              <strong>Estimated Time:</strong> {deploymentStatus?.estimated_time}
            </p>
            {deploymentType === 'local' && (
              <p>
                <strong>Endpoint:</strong> {deploymentStatus?.endpoint}
              </p>
            )}
          </div>
          
          <div className="deployment-actions">
            <button
              className="view-logs-button"
              onClick={() => window.open(deploymentStatus?.logs_url, '_blank')}
            >
              View Logs
            </button>
            <button
              className="done-button"
              onClick={onCancel}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeploymentOptions;